<template>
  <Dialog 
    v-model:visible="visible" 
    header="Welcome to UNIBO ML Exam Practice!" 
    :modal="true" 
    class="w-full max-w-xl bg-slate-900 border border-slate-800 text-slate-100"
  >
    <div class="space-y-4 p-2 select-none text-slate-300 text-sm">
      <div class="p-3 bg-unibo-red/10 border border-unibo-red/30 rounded-xl flex items-center space-x-3">
        <div class="w-10 h-10 rounded-lg bg-unibo-red text-white flex items-center justify-center font-bold text-lg shrink-0">
          ML
        </div>
        <div>
          <h3 class="font-semibold text-slate-100 text-sm">In-Browser Python Lab Environment</h3>
          <p class="text-xs text-slate-400">Offline-capable exam preparation for University of Bologna Machine Learning course.</p>
        </div>
      </div>

      <div class="space-y-3 font-normal text-xs leading-relaxed">
        <div class="flex items-start space-x-3 p-2.5 rounded-lg bg-slate-800/60 border border-slate-800">
          <i class="pi pi-desktop text-sky-400 text-lg mt-0.5 shrink-0"></i>
          <div>
            <strong class="text-slate-200 block mb-0.5">1. Python Pyodide Runtime</strong>
            Code executes 100% in your browser using WebAssembly. Preloaded with <code class="text-sky-300">numpy</code>, <code class="text-sky-300">scipy</code>, <code class="text-sky-300">pandas</code>, <code class="text-sky-300">matplotlib</code>, <code class="text-sky-300">seaborn</code>, <code class="text-sky-300">scikit-learn</code>, and <code class="text-sky-300">mlxtend</code>.
          </div>
        </div>

        <div class="flex items-start space-x-3 p-2.5 rounded-lg bg-slate-800/60 border border-slate-800">
          <i class="pi pi-database text-amber-400 text-lg mt-0.5 shrink-0"></i>
          <div>
            <strong class="text-slate-200 block mb-0.5">2. Dataset Auto-Mounting</strong>
            Exam datasets (e.g. <code class="text-amber-300">seeds_dataset.txt</code>, <code class="text-amber-300">exam2022_01_13.csv</code>) are mounted directly into Pyodide's virtual filesystem. Run <code class="text-amber-300">pd.read_csv('filename.csv')</code> immediately!
          </div>
        </div>

        <div class="flex items-start space-x-3 p-2.5 rounded-lg bg-slate-800/60 border border-slate-800">
          <i class="pi pi-lightbulb text-purple-400 text-lg mt-0.5 shrink-0"></i>
          <div>
            <strong class="text-slate-200 block mb-0.5">3. Reference Solution & Professor Rubric Checklist</strong>
            Toggle the spoilered reference solution for guidance, and self-assess using the professor-oriented checklist per question.
          </div>
        </div>

        <div class="flex items-start space-x-3 p-2.5 rounded-lg bg-slate-800/60 border border-slate-800">
          <i class="pi pi-save text-emerald-400 text-lg mt-0.5 shrink-0"></i>
          <div>
            <strong class="text-slate-200 block mb-0.5">4. Local Browser Persistence</strong>
            All code drafts, snapshots, preferences, and checklist states auto-save in IndexedDB. Use Export/Import to transfer data across devices.
          </div>
        </div>
      </div>

      <div class="flex justify-end pt-2">
        <Button @click="visible = false" severity="primary" class="!bg-unibo-red hover:!bg-unibo-darkRed !px-6">
          Get Started
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits(['update:visible']);

const visible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val),
});
</script>
